import React, { useState } from "react";
import moment from "moment";
import { Alert, DialogActions, Grid } from "@mui/material";
import { FileUploader } from "react-drag-drop-files";
import DialogLayout from "../../../Components/DialogLayout";
import ButtonField from "../../../Components/Ui/ButtonField";
import FormInput from "../../../Components/Ui/FormInput";
import FormSelect from "../../../Components/FormFields/FormSelect";
import { useTailwindBrand } from "../../../Components/shared";
import TransportSubscriptionRepository, {
    TRANSPORT_SUBSCRIPTION_TYPES,
    TransportSubscriptionInfo,
} from "../../../Repositories/TransportSubscriptionRepository";
import Teachr from "../../../Objects/models/Teachr";
import { getActiveClientBrandConfig } from "../../../config/environment.config";

const FILE_TYPES = ["PDF", "PNG", "JPEG", "JPG"];

export interface SubscriptionClientOption {
    id: number;
    name: string;
}

interface TransportSubscriptionUploadProps {
    open: boolean;
    teachr: Teachr;
    onClose: () => void;
    onSaved: (subscription: TransportSubscriptionInfo) => void;
    /** P6 : période du cours en cours de déclaration, rappelée au prof. */
    coursePeriod?: { start?: Date | string | null; end?: Date | string | null };
    /** Client du couple quand le contexte le fixe (déclaration, fiche client). */
    clientId?: number | null;
    clientName?: string | null;
    /** Sans client fixé : liste dans laquelle le prof choisit le client concerné. */
    clientOptions?: SubscriptionClientOption[];
}

/**
 * P6 : dépôt d'un justificatif d'abonnement de transports en commun (Navigo,
 * TER, autre) avec sa période de validité déclarée, rattaché au couple
 * prof/client. Un seul dépôt couvre ensuite toutes les déclarations de ce
 * client sur la période : plus de preuve à joindre à chaque cours tant que
 * l'abonnement est valide.
 */
const TransportSubscriptionUpload = ({ open, teachr, onClose, onSaved, coursePeriod, clientId, clientName, clientOptions }: TransportSubscriptionUploadProps) => {
    const { common, brandConfig } = useTailwindBrand();
    const [file, setFile] = useState<File | null>(null);
    const [type, setType] = useState<string>("");
    const [selectedClientId, setSelectedClientId] = useState<number | null>(clientId ?? null);
    const mustPickClient = clientId == null && (clientOptions?.length ?? 0) > 0;
    const effectiveClientId = clientId ?? selectedClientId;
    const effectiveClientName = clientName
        ?? clientOptions?.find((c) => c.id === selectedClientId)?.name
        ?? null;
    const [validFrom, setValidFrom] = useState("");
    const [validUntil, setValidUntil] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    // Rappel de la période à couvrir quand le dépôt intervient pendant une
    // déclaration : la saisie doit l'englober pour servir de preuve.
    const periodStart = coursePeriod?.start ? moment(coursePeriod.start) : null;
    const periodEnd = coursePeriod?.end ? moment(coursePeriod.end) : null;
    const hasPeriod = !!(periodStart?.isValid() && periodEnd?.isValid());

    const validate = (): string => {
        if (mustPickClient && selectedClientId == null) return `Veuillez choisir ${getActiveClientBrandConfig("", "l'élève", "le client")} concerné par cet abonnement.`;
        if (!type) return "Veuillez sélectionner le type d'abonnement.";
        if (!validFrom || !validUntil) return "Veuillez renseigner la période de validité.";
        const from = moment(validFrom, "YYYY-MM-DD", true);
        const until = moment(validUntil, "YYYY-MM-DD", true);
        if (!from.isValid() || !until.isValid()) return "Les dates de validité sont invalides.";
        if (until.isBefore(from)) return "La date de fin doit être postérieure à la date de début.";
        if (until.isBefore(moment().startOf("day"))) return "Cet abonnement est déjà expiré.";
        if (until.isAfter(from.clone().add(12, "months"))) return "Un abonnement ne peut pas couvrir plus de 12 mois.";
        // Déposé pendant une déclaration : il doit couvrir toute la période du
        // cours, sans quoi il ne pourra pas servir de preuve.
        if (hasPeriod && (periodStart!.isBefore(from, "day") || periodEnd!.isAfter(until, "day"))) {
            return `Cette période ne couvre pas votre cours du ${periodStart!.format("DD/MM/YYYY")} au ${periodEnd!.format("DD/MM/YYYY")}.`;
        }
        if (!file) return "Le justificatif est obligatoire (abonnement, attestation de l'opérateur...).";
        return "";
    };

    const handleConfirm = async () => {
        const message = validate();
        if (message) {
            setError(message);
            return;
        }
        setError("");
        if (!teachr?.id || !file) return;
        setLoading(true);
        try {
            const subscription = await TransportSubscriptionRepository.create(teachr.id, file, type, validFrom, validUntil, effectiveClientId);
            onSaved(subscription);
        } catch (e: any) {
            setError("L'enregistrement du justificatif a échoué. Vérifiez les informations puis réessayez.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <DialogLayout
            title="Justificatif de transports en commun 🚌"
            open={open}
            handleClose={onClose}
            handleCloseIcon={onClose}
            customDialogActions="hidden"
            maxWidth="md"
            text={
                <div className={`${common.body} space-y-4`}>
                    <p>
                        Déposez votre abonnement (Navigo, TER...) avec sa période de validité : il servira
                        automatiquement de preuve de déplacement pour toutes vos déclarations
                        {effectiveClientName ? <> pour <span className="font-semibold">{effectiveClientName}</span></> : null} couvertes par
                        cette période, sans rien re-joindre à chaque cours.
                    </p>

                    {mustPickClient && (
                        <FormSelect
                            label={`${getActiveClientBrandConfig("", "Élève", "Client")} concerné`}
                            placeholder="Sélectionner"
                            data={clientOptions!.map((c) => c.name)}
                            defaultValue={selectedClientId != null ? clientOptions!.findIndex((c) => c.id === selectedClientId) + 1 : ""}
                            callback={(index: number) => setSelectedClientId(clientOptions![index - 1]?.id ?? null)}
                            required
                        />
                    )}

                    {hasPeriod && (
                        <Alert severity="warning" variant="outlined" sx={{ fontFamily: brandConfig.fontFamily }}>
                            Pour servir de preuve à la déclaration en cours, l'abonnement doit couvrir la
                            période du <span className="font-semibold">{periodStart!.format("DD/MM/YYYY")}</span> au{" "}
                            <span className="font-semibold">{periodEnd!.format("DD/MM/YYYY")}</span>.
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <FormSelect
                                label="Type d'abonnement"
                                placeholder="Sélectionner"
                                data={TRANSPORT_SUBSCRIPTION_TYPES.map((t) => t.label)}
                                defaultValue={type ? TRANSPORT_SUBSCRIPTION_TYPES.findIndex((t) => t.value === type) + 1 : ""}
                                callback={(index: number) => setType(TRANSPORT_SUBSCRIPTION_TYPES[index - 1]?.value ?? "")}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormInput
                                label="Valide du"
                                type="date"
                                value={validFrom}
                                onChange={(e) => setValidFrom(e.target.value)}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <FormInput
                                label="Valide jusqu'au"
                                type="date"
                                value={validUntil}
                                onChange={(e) => setValidUntil(e.target.value)}
                                required
                            />
                        </Grid>
                    </Grid>

                    <FileUploader
                        handleChange={(f: File) => { setFile(f); setError(""); }}
                        name="abonnement"
                        types={FILE_TYPES}
                        label="Déposez ou cliquez pour ajouter votre justificatif d'abonnement"
                    />
                    {file && (
                        <p className="text-sm">
                            Fichier sélectionné : <span className="font-semibold">{file.name}</span>
                        </p>
                    )}

                    <Alert severity="info" variant="outlined" sx={{ fontFamily: brandConfig.fontFamily }}>
                        Les dates saisies sont déclaratives, le document fait foi : il sera visible par
                        notre équipe lors de la validation de vos déclarations. Vous serez prévenu par
                        e-mail 7 jours avant l'expiration.
                    </Alert>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                </div>
            }
            btnChildren={
                <DialogActions sx={{ py: 2.5, px: 3, borderTop: "1px solid", borderColor: "divider" }} className={common.body}>
                    <ButtonField variant="outlined" onClick={onClose} label="Annuler" disabled={loading} />
                    <ButtonField variant="contained" onClick={handleConfirm} label="Enregistrer mon abonnement" loading={loading} disabled={loading} />
                </DialogActions>
            }
        />
    );
};

export default TransportSubscriptionUpload;
